
import { useEffect, useMemo, useState } from "react";

import { apiRequest, ensureCsrfToken } from "../../../api";

import "../../../css/MappingHeadersView.css";

import Swal from "sweetalert2";


const MAPPING_TYPES = [
  { key: "wholdtax_master", label: "WithHold Tax Master" },
  { key: "gsttax_master", label: "GST Tax Master" },
];


function buildEmptySelections(context) {
  return {
    wholdtax_master: Object.fromEntries(
      (context.wholdtax_system_variables || []).map((item) => [
        item.name,
        {
          header: "",
          mapped_type: "",
        },
      ]),
    ),

    gsttax_master: Object.fromEntries(
      (context.gsttax_system_variables || []).map((item) => [
        item.name,
        {
          header: "",
          mapped_type: "",
        },
      ]),
    ),
  };
}


export default function NonPoMappingHeadersView() {
  const [context, setContext] = useState(null);
  const [activeType, setActiveType] = useState("wholdtax_master");
  const [selections, setSelections] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const [validationRows, setValidationRows] = useState(new Set());


  /*
   * Get system variables for currently selected mapping type
   */
  const currentRows = useMemo(() => {
    if (!context) {
      return [];
    }

    if (activeType === "wholdtax_master") {
      return context.wholdtax_system_variables || [];
    }

    if (activeType === "gsttax_master") {
      return context.gsttax_system_variables || [];
    }

    return [];
  }, [activeType, context]);


  /*
   * Get ERP headers for currently selected mapping type
   */
  const currentHeaders = useMemo(() => {
    if (!context) {
      return [];
    }

    if (activeType === "wholdtax_master") {
      return context.wholdtax_master_headers || [];
    }

    if (activeType === "gsttax_master") {
      return context.gsttax_master_headers || [];
    }

    return [];
  }, [activeType, context]);


  /*
   * Load mapping context
   */
  useEffect(() => {
    let cancelled = false;

    async function loadContext() {
      setLoading(true);
      setError("");

      try {
        const response = await apiRequest("/mapping/context/");

        if (cancelled) {
          return;
        }

        const nextContext = response.data;

        setContext(nextContext);
        setSelections(buildEmptySelections(nextContext));

      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.message ||
              "Unable to load mapping context."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadContext();

    return () => {
      cancelled = true;
    };
  }, []);


  /*
   * Load saved mappings whenever mapping type changes
   */
  useEffect(() => {
    let cancelled = false;

    async function loadSavedMapping() {
      if (!context || !selections) {
        return;
      }

      setStatus("");
      setError("");

      try {
        const csrfToken = await ensureCsrfToken();

        const response = await fetch("/api/mapping/data/", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            type: activeType,
          }),
        });

        const payload = await response.json();

        if (!response.ok || !payload.success) {
          throw new Error(
            payload.error || "Unable to load saved mappings."
          );
        }

        if (cancelled) {
          return;
        }

        setSelections((current) => ({
          ...current,
          [activeType]: {
            ...current[activeType],
            ...payload.data,
          },
        }));

      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.message ||
              "Unable to load saved mappings."
          );
        }
      }
    }

    loadSavedMapping();

    return () => {
      cancelled = true;
    };
  }, [activeType, context, selections ? "ready" : "empty"]);


  /*
   * Update selected ERP header
   */
  function updateSelection(variableName, value) {
    setSelections((current) => ({
      ...current,
      [activeType]: {
        ...current[activeType],
        [variableName]: {
          header: value,
          mapped_type: value ? "mapped" : "",
        },
      },
    }));
  }


  /*
   * Validate mandatory mappings
   */
  function validateSelections() {
    const errors = [];
    const invalidRows = [];

    currentRows.forEach((row) => {
      const mapping = selections[activeType]?.[row.name];

      if (row.mandatory && !mapping?.header) {
        errors.push(`${row.name}: mapping is required`);
        invalidRows.push(row.name);
      }
    });

    return {
      errors,
      invalidRows,
    };
  }


  /*
   * Build API payload
   */
  function buildSavePayload() {
    return {
      type: activeType,
      mappings: [],
      mappingss: Object.fromEntries(
        Object.entries(selections[activeType] || {}).map(
          ([key, value]) => [
            key,
            value?.mapped_type === "mapped"
              ? value.header
              : "",
          ],
        ),
      ),
    };
  }


  /*
   * Save mappings
   */
  async function handleSave() {
    setSaving(true);
    setError("");

    try {
      const validationResult = validateSelections();

      setValidationRows(
        new Set(validationResult.invalidRows)
      );

      if (validationResult.errors.length) {
        if (validationResult.invalidRows.length) {
          document
            .querySelector(
              `[data-row="${validationResult.invalidRows[0]}"]`
            )
            ?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
        }

        throw new Error(
          validationResult.errors.join(" | ")
        );
      }

      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/mapping/save/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(buildSavePayload()),
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(
          payload.message || "Unable to save mappings."
        );
      }

      setValidationRows(new Set());

      await Swal.fire({
        icon: "success",
        title: "Success",
        text:
          payload.message ||
          "Mappings saved successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {
      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          requestError.message ||
          "Unable to save mappings.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
          "Unable to save mappings."
      );

    } finally {
      setSaving(false);
    }
  }


  if (loading) {
    return (
      <div className="workspace-panel">
        Loading header mapping…
      </div>
    );
  }


  if (!context || !selections) {
    return (
      <div className="workspace-panel">
        Mapping context is unavailable.
      </div>
    );
  }


  return (
    <section className="mapping-view">

      {/* Header */}
      <div className="workspace-panel mapping-view__hero">

        <div>
          <p className="eyebrow">Header Mapping</p>

          <h2>Mapping Configuration</h2>

          <p className="config-view__intro">
            Match uploaded ERP headers to the system
            variables used by the Django backend
            validations.
          </p>
        </div>


        <div className="mapping-view__controls">

          <label className="config-field">

            <span>Mapping Type</span>

            <select
              value={activeType}
              onChange={(event) =>
                setActiveType(event.target.value)
              }
            >
              {MAPPING_TYPES.map((option) => (
                <option
                  key={option.key}
                  value={option.key}
                >
                  {option.label}
                </option>
              ))}
            </select>

          </label>


          <button
            className="primary-button"
            type="button"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save Mapping"}
          </button>

        </div>

      </div>


      {/* Messages */}
      {status ? (
        <div className="config-banner config-banner--success">
          {status}
        </div>
      ) : null}


      {error ? (
        <div className="config-banner config-banner--error">
          {error}
        </div>
      ) : null}


      {/* Mapping Table */}
      <div className="workspace-panel">

        <div className="config-table">

          <table>

            <thead>
              <tr>
                <th>System Variable</th>
                <th>ERP Header</th>
              </tr>
            </thead>


            <tbody>

              {currentRows.map((row) => {

                const mapping =
                  selections[activeType]?.[row.name];

                return (
                  <tr
                    key={row.name}
                    data-row={row.name}
                    className={
                      validationRows.has(row.name)
                        ? "mapping-row-error"
                        : ""
                    }
                  >

                    <td>

                      <div className="mapping-variable">

                        <span>{row.name}</span>

                        {row.mandatory ? (
                          <span className="mandatory-star">
                            *
                          </span>
                        ) : null}

                        {row.conditional_mandatory ? (
                          <span className="mandatory-star">
                            **
                          </span>
                        ) : null}

                      </div>


                      {row.tooltip ? (
                        <div className="mapping-tooltip">
                          {row.tooltip}
                        </div>
                      ) : null}

                    </td>


                    <td>

                      <select
                        className="mapping-select"
                        value={mapping?.header || ""}
                        onChange={(event) =>
                          updateSelection(
                            row.name,
                            event.target.value
                          )
                        }
                      >

                        <option value="">
                          -- Select Header --
                        </option>

                        {currentHeaders.map((header) => (
                          <option
                            key={header}
                            value={header}
                          >
                            {header}
                          </option>
                        ))}

                      </select>


                      {mapping?.mapped_type === "mapped" ? (
                        <span className="mapping-badge mapping-badge--mapped">
                          ✓ Saved
                        </span>
                      ) : null}


                      {mapping?.mapped_type === "suggestion" ? (
                        <span className="mapping-badge mapping-badge--suggested">
                          💡 Suggested
                        </span>
                      ) : null}

                    </td>

                  </tr>
                );
              })}

            </tbody>

          </table>

        </div>

      </div>

    </section>
  );
}

